import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  SearchIcon,
  ShieldCheckIcon,
  ShieldOffIcon,
  UserIcon,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  getApiAdminUsersOptions,
  getApiAdminUsersQueryKey,
  patchApiAdminUsersByIdMutation,
} from '@/lib/api/client/@tanstack/react-query.gen'
import { cn } from '@/lib/utils'

const ALL_MODULES: Array<{ key: string; label: string }> = [
  { key: 'guests', label: 'รายชื่อแขก' },
  { key: 'tables', label: 'จัดการโต๊ะ' },
  { key: 'blessings', label: 'คำอวยพร' },
  { key: 'angpao', label: 'อั่งเปา' },
  { key: 'random-number', label: 'สุ่มหมายเลข' },
  { key: 'settings', label: 'ตั้งค่า' },
  { key: 'wall', label: 'Wall' },
  { key: 'richmenu', label: 'จัดการ Rich Menu' },
]

type UserRecord = {
  userId: string
  displayName: string
  pictureUrl: string | null
  role: 'admin' | null
  allowedModules: string[] | null
}

type EditorState = {
  user: UserRecord
  isSuperAdmin: boolean
  selectedModules: string[]
}

export const UsersPage = () => {
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery(getApiAdminUsersOptions())
  const [editor, setEditor] = useState<EditorState | null>(null)
  const [confirmRevoke, setConfirmRevoke] = useState(false)
  const [search, setSearch] = useState('')

  const mutation = useMutation({
    ...patchApiAdminUsersByIdMutation(),
    onSuccess: () => {
      toast.success('อัปเดตสิทธิ์สำเร็จ')
      queryClient.invalidateQueries({ queryKey: getApiAdminUsersQueryKey() })
      setEditor(null)
      setConfirmRevoke(false)
    },
    onError: () => {
      toast.error('ไม่สามารถอัปเดตสิทธิ์ได้')
    },
  })

  const users = (data?.users ?? []) as UserRecord[]
  const admins = users.filter((u) => u.role === 'admin')
  const nonAdmins = users
    .filter((u) => u.role !== 'admin')
    .filter((u) => u.displayName.toLowerCase().includes(search.toLowerCase()))

  const openEditor = (user: UserRecord) => {
    setConfirmRevoke(false)
    setEditor({
      user,
      isSuperAdmin: user.allowedModules === null,
      selectedModules: user.allowedModules ?? [],
    })
  }

  const openPromoter = (user: UserRecord) => {
    setConfirmRevoke(false)
    setEditor({
      user,
      isSuperAdmin: false,
      selectedModules: [],
    })
  }

  const handleSave = () => {
    if (!editor) return
    const allowedModules = editor.isSuperAdmin ? null : editor.selectedModules
    mutation.mutate({
      path: { id: editor.user.userId },
      body: { role: 'admin', allowedModules },
    })
  }

  const handleRevoke = () => {
    if (!editor) return
    mutation.mutate({
      path: { id: editor.user.userId },
      body: { role: null, allowedModules: null },
    })
  }

  const toggleModule = (key: string) => {
    if (!editor) return
    setEditor((prev) => {
      if (!prev) return prev
      const has = prev.selectedModules.includes(key)
      return {
        ...prev,
        selectedModules: has
          ? prev.selectedModules.filter((m) => m !== key)
          : [...prev.selectedModules, key],
      }
    })
  }

  if (isLoading) {
    return (
      <div className="text-muted-foreground p-8 text-center">กำลังโหลด...</div>
    )
  }

  return (
    <div className="bg-background-light flex flex-1 flex-col overflow-hidden">
      <header className="bg-white px-8 pt-6 pb-6 shadow-xs">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">
              ผู้ดูแลระบบ
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              จัดการสิทธิ์การเข้าถึงของผู้ดูแลระบบ
            </p>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto px-8 py-6">
        <div className="mx-auto max-w-3xl space-y-8">
          {/* Admins section */}
          <section>
            <h2 className="mb-4 text-lg font-semibold text-gray-700">
              ผู้ดูแลระบบ ({admins.length})
            </h2>
            {admins.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                ยังไม่มีผู้ดูแลระบบ
              </p>
            ) : (
              <div className="space-y-3">
                {admins.map((user) => (
                  <AdminUserRow
                    key={user.userId}
                    user={user}
                    onEdit={() => openEditor(user)}
                  />
                ))}
              </div>
            )}
          </section>

          {/* All users section */}
          <section>
            <div className="mb-4 flex items-center justify-between gap-4">
              <h2 className="text-lg font-semibold text-gray-700">
                ผู้ใช้อื่น ({nonAdmins.length})
              </h2>
              <div className="relative w-56">
                <SearchIcon className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="ค้นหาผู้ใช้..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            {nonAdmins.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-8 text-center">
                {search ? (
                  <p className="text-sm font-medium text-gray-600">
                    ไม่พบผู้ใช้ที่ตรงกับ "{search}"
                  </p>
                ) : (
                  <>
                    <p className="text-sm font-medium text-gray-600">
                      ยังไม่มีผู้ใช้อื่นในระบบ
                    </p>
                    <p className="mt-1 text-xs text-gray-400">
                      ให้ผู้ที่ต้องการเป็นผู้ดูแลเข้าสู่ระบบผ่าน LINE ก่อน
                      จากนั้นจะปรากฏที่นี่
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {nonAdmins.map((user) => (
                  <NonAdminUserRow
                    key={user.userId}
                    user={user}
                    onPromote={() => openPromoter(user)}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </main>

      {/* Edit / Promote sheet */}
      <Sheet
        open={!!editor}
        onOpenChange={(open) => {
          if (!open) {
            setEditor(null)
            setConfirmRevoke(false)
          }
        }}
      >
        <SheetContent className="flex flex-col gap-6 overflow-y-auto p-6">
          {editor && (
            <>
              <SheetHeader>
                <SheetTitle>
                  {editor.user.role === 'admin'
                    ? 'แก้ไขสิทธิ์ผู้ดูแล'
                    : 'เพิ่มผู้ดูแลระบบ'}
                </SheetTitle>
              </SheetHeader>

              <div className="flex items-center gap-3">
                <Avatar className="size-12">
                  <AvatarImage src={editor.user.pictureUrl ?? undefined} />
                  <AvatarFallback>
                    <UserIcon className="size-6" />
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold">{editor.user.displayName}</p>
                </div>
              </div>

              {/* Role toggle */}
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-700">
                  ประเภทผู้ดูแล
                </p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      setEditor((prev) =>
                        prev ? { ...prev, isSuperAdmin: true } : prev,
                      )
                    }
                    className={cn(
                      'flex flex-1 flex-col items-center rounded-xl border-2 px-4 py-3 text-center text-sm font-medium transition-colors',
                      editor.isSuperAdmin
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-gray-200 text-gray-500 hover:border-gray-300',
                    )}
                  >
                    Super Admin
                    <span className="mt-0.5 block text-xs font-normal opacity-70">
                      เข้าถึงได้ทุกโมดูล
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setEditor((prev) =>
                        prev ? { ...prev, isSuperAdmin: false } : prev,
                      )
                    }
                    className={`flex flex-1 flex-col items-center rounded-xl border-2 px-4 py-3 text-center text-sm font-medium transition-colors ${
                      !editor.isSuperAdmin
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    Limited Admin
                    <span className="mt-0.5 block text-xs font-normal opacity-70">
                      เฉพาะโมดูลที่กำหนด
                    </span>
                  </button>
                </div>
              </div>

              {/* Module checklist */}
              {!editor.isSuperAdmin && (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700">
                    โมดูลที่เข้าถึงได้
                  </p>
                  <div className="space-y-2">
                    {ALL_MODULES.map((mod) => (
                      <label
                        key={mod.key}
                        className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-100 px-4 py-3 hover:bg-gray-50"
                      >
                        <Checkbox
                          checked={editor.selectedModules.includes(mod.key)}
                          onCheckedChange={() => toggleModule(mod.key)}
                        />
                        <Label className="cursor-pointer text-sm">
                          {mod.label}
                        </Label>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <SheetFooter className="mt-auto flex-col gap-2">
                <Button
                  onClick={handleSave}
                  disabled={
                    mutation.isPending ||
                    (!editor.isSuperAdmin &&
                      editor.selectedModules.length === 0)
                  }
                  className="w-full"
                >
                  {mutation.isPending ? 'กำลังบันทึก...' : 'บันทึก'}
                </Button>

                {editor.user.role === 'admin' && (
                  <>
                    {confirmRevoke ? (
                      <div className="space-y-2">
                        <p className="text-center text-sm text-red-600">
                          ยืนยันการถอนสิทธิ์ผู้ดูแล?
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => setConfirmRevoke(false)}
                          >
                            ยกเลิก
                          </Button>
                          <Button
                            variant="destructive"
                            className="flex-1"
                            disabled={mutation.isPending}
                            onClick={handleRevoke}
                          >
                            ถอนสิทธิ์
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        className="w-full text-red-500 hover:text-red-600"
                        onClick={() => setConfirmRevoke(true)}
                      >
                        <ShieldOffIcon className="mr-2 size-4" />
                        ถอนสิทธิ์ผู้ดูแล
                      </Button>
                    )}
                  </>
                )}
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}

function AdminUserRow({
  user,
  onEdit,
}: {
  user: UserRecord
  onEdit: () => void
}) {
  const isSuperAdmin = user.allowedModules === null

  return (
    <div className="flex items-center gap-4 rounded-2xl bg-white px-5 py-4 shadow-sm">
      <Avatar className="size-10 shrink-0">
        <AvatarImage src={user.pictureUrl ?? undefined} />
        <AvatarFallback>
          <UserIcon className="size-5" />
        </AvatarFallback>
      </Avatar>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate font-medium text-gray-900">
            {user.displayName}
          </span>
          <Badge variant={isSuperAdmin ? 'default' : 'secondary'}>
            {isSuperAdmin ? 'Super' : 'Limited'}
          </Badge>
        </div>
        {!isSuperAdmin && user.allowedModules && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {user.allowedModules.map((mod) => {
              const label = ALL_MODULES.find((m) => m.key === mod)?.label ?? mod
              return (
                <span
                  key={mod}
                  className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-600"
                >
                  {label}
                </span>
              )
            })}
          </div>
        )}
        {isSuperAdmin && (
          <p className="mt-0.5 text-xs text-gray-400">เข้าถึงได้ทุกโมดูล</p>
        )}
      </div>

      <Button
        variant="outline"
        size="sm"
        className="shrink-0"
        onClick={onEdit}
      >
        <ShieldCheckIcon className="mr-1.5 size-4" />
        แก้ไข
      </Button>
    </div>
  )
}

function NonAdminUserRow({
  user,
  onPromote,
}: {
  user: UserRecord
  onPromote: () => void
}) {
  return (
    <div className="flex items-center gap-4 rounded-2xl bg-white px-5 py-4 shadow-sm">
      <Avatar className="size-10 shrink-0">
        <AvatarImage src={user.pictureUrl ?? undefined} />
        <AvatarFallback>
          <UserIcon className="size-5" />
        </AvatarFallback>
      </Avatar>

      <div className="min-w-0 flex-1">
        <span className="truncate font-medium text-gray-900">
          {user.displayName}
        </span>
      </div>

      <Button
        variant="outline"
        size="sm"
        className="shrink-0"
        onClick={onPromote}
      >
        <ShieldCheckIcon className="mr-1.5 size-4" />
        แต่งตั้ง
      </Button>
    </div>
  )
}
